import { useState } from "react";

import {
  makeDeleteApiCall,
  makeGetApiCall,
  makePostApiCall,
  makePostApiCallToPrinter,
  makePutApiCall,
} from "../../shared/makeApiCall";
import { fetchUsersForDropdown } from "../../shared/usersForDropdown";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
import { SnackbarData, SnackbarType } from "../../components/Snackbar";
import { generateUniqueNumber } from "../../shared/utility";
import ProgressModal from "../../components/modals/ProgressModal";

export function ShippingDetailsViewModel(shippingId, logout, addSnackbar) {
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });
  const [shipping, setShipping] = useState(null);
  const [loading, setLoading] = useState(true);
  const [slideOver, setSlideOver] = useState({
    childComponent: null,
    open: false,
  });

  const showProgressModal = (message = "Processing...") => {
    setEmptyModelOpen({
      childComponent: <ProgressModal message={message} />,
      open: true,
    });
  };

  const hideProgressModal = () => {
    setEmptyModelOpen({
      childComponent: null,
      open: false,
    });
  };

  const getShippingDetails = async function () {
    let callResult = await makePostApiCall(`fetchShipping/${shippingId}`);
    if (callResult.success) {
      const orderDetails = await onFetchOrderItems(callResult.data.order_id);
      if (orderDetails.success) {
        const order = orderDetails.data;
        const shipmentItemsResult = await makeGetApiCall(
          `getShippingItems/${shippingId}`,
        );
        const shipmentLines = Array.isArray(
          shipmentItemsResult.data?.shipment_items,
        )
          ? shipmentItemsResult.data.shipment_items
          : [];
        const shipmentLinesById = new Map(
          shipmentLines.map((item) => [Number(item.id), item]),
        );
        setShipping({
          ...callResult.data,
          order_id: order.id,
          customer: order.customer,
          orderItems: order.products.map((item) => ({
            ...item,
            ...(shipmentLinesById.get(Number(item.id)) ?? {}),
          })),
        });

        setLoading(false);
      } else {
        setLoading(false);
      }
    } else {
      setLoading(false);
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
  };

  const onFetchOrderItems = async function (id) {
    let callResult = await makePostApiCall(`fetchOrder/${id}`);
    if (callResult.success) {
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const showErrorModal = (title, subtitle) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title={title}
        />
      ),
      open: true,
    });
  };

  const updateShippingApiCall = async function (payload) {
    const nextStatus =
      typeof payload?.status === "object"
        ? payload.status?.value
        : payload?.status;
    const currentStatus =
      typeof shipping?.status === "object"
        ? shipping.status?.value
        : (shipping?.status ?? shipping?.legacy_order_status);
    const requestPayload = {
      ...payload,
      status: payload.status,
    };
    let callResult = await makePostApiCall(
      `updateShipping/${shippingId}`,
      requestPayload,
    );
    if (callResult.success) {
      const shouldCreateInvoiceInQb = nextStatus === 5 && currentStatus !== 5;

      if (shouldCreateInvoiceInQb) {
        await createInvoiceInQb();
      } else {
        addSnackbar(
          new SnackbarData(
            generateUniqueNumber(),
            callResult.data.message,
            SnackbarType.Success,
          ),
        );
      }

      getShippingDetails();
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
  };

  const onFetchPackagedItemByBarcode = async function (scannedBarcode) {
    let callResult = await makePostApiCall(
      `fetchPackagedItemByBarcode/${scannedBarcode}`,
    );
    if (callResult.success) {
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const addPackagedItemToOrderItem = async function (
    shippingId,
    orderItem,
    packagedItem,
  ) {
    const payload = {
      shippingId: shippingId,
      orderItem: orderItem,
      packagedItem: packagedItem,
    };
    let callResult = await makePostApiCall(
      "addPackagedItemToOrderItem",
      payload,
    );
    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Package added to shipping",
          SnackbarType.Success,
        ),
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const removeShippedCartonApiCall = async function (carton) {
    const callResult = await makePostApiCall(`removeShippedCarton`, {
      id: carton.id,
      barcode: carton.barcode,
      carton_type: carton.carton_type,
    });
    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          callResult.data.message,
          SnackbarType.Success,
        ),
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const getUsersForDropdown = async function (type, department) {
    let callResult = await fetchUsersForDropdown(type, department);

    if (callResult.success) {
      setEmployeeOptions(callResult.data);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const deleteShipping = async function () {
    let callResult = await makePostApiCall(`shippings/delete/${shippingId}`);

    if (callResult.success) {
      window.location = "/shippings";
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.error);
    }
  };

  const printShipping = async function () {
    let callResult = await makePostApiCall(`printShipping/${shippingId}`);

    if (callResult.success) {
      window.open(callResult.data.location, "_blank");
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
  };

  const quickAddPackagedItemToOrderItem = async function (
    shippingId,
    orderItemId,
    lotItem,
    original_scanned_amount,
  ) {
    const payload = {
      shippingId: shippingId,
      orderItemId: orderItemId,
      lotItem: lotItem,
      original_scanned_amount: original_scanned_amount,
    };
    let callResult = await makePostApiCall(
      "quickAddPackagedItemToOrderItem",
      payload,
    );
    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Package(s) added to shipping",
          SnackbarType.Success,
        ),
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const fetchPackagedItemsForQuickScan = async function (payload) {
    let callResult = await makePostApiCall(
      `fetchPackagedItemsForQuickScan`,
      payload,
    );
    if (callResult.success) {
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const createInvoiceInQb = async function () {
    showProgressModal("Sending shipping to Quickbooks...");
    let callResult = await makePostApiCall(`quickbooks/createInvoice`, {
      shipment_id: shippingId,
    });
    hideProgressModal();
    if (callResult.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Shipping sent to Quickbooks",
          SnackbarType.Success,
        ),
      );
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const normalizeFlatShipmentItems = function (items = []) {
    return items.map((item) => ({
      id: item.id,
      shipment_item_id: item.id,
      barcode: item.barcode,
      lot_number: item.lot_number,
      packing_size: item.packing_size,
      packaging_date: item.picked_at,
      case_quantity: item.case_quantity ?? 0,
      carton_type: item.carton_type,
      product_id: item.product_id,
      product_name: item.product_name,
      plu: item.plu,
      serial_number: item.serial_number,
      status: item.status,
      return_status: item.return_status,
      credit_status: item.credit_status,
      description_en: item.description,
      product_temperature: item.product_temperature,
      order_item_id: item.order_item_id,
      is_quick_scan: item.is_quick_scan ?? false,
    }));
  };

  const getOrderItemShipments = async function (id) {
    const callResult = await makeGetApiCall(`getShippingItems/${shippingId}`);
    if (callResult.success) {
      const flatItems = Array.isArray(callResult.data?.flat_shipment_items)
        ? callResult.data.flat_shipment_items
        : [];
      const filteredItems = flatItems.filter(
        (item) => parseInt(item.order_item_id, 10) === parseInt(id, 10),
      );
      return { success: true, data: normalizeFlatShipmentItems(filteredItems) };
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
      return { success: false };
    }
  };

  const handlePriceSplitResult = async (callResult, message) => {
    if (callResult.success) {
      addSnackbar(
        new SnackbarData(generateUniqueNumber(), message, SnackbarType.Success),
      );
      await getShippingDetails();
    } else {
      if (callResult.errorStatus === 401) logout();
      showErrorModal(
        callResult.data?.message ?? "Unable to update shipment pricing.",
      );
    }
    return callResult;
  };

  const onCreatePriceSplit = async (orderItemId, payload) =>
    handlePriceSplitResult(
      await makePostApiCall(
        `shippings/${shippingId}/orderItems/${orderItemId}/priceSplits`,
        payload,
      ),
      "Shipment price added",
    );

  const onUpdatePriceSplit = async (orderItemId, splitId, payload) =>
    handlePriceSplitResult(
      await makePutApiCall(
        `shippings/${shippingId}/orderItems/${orderItemId}/priceSplits/${splitId}`,
        payload,
      ),
      "Shipment price updated",
    );

  const onDeletePriceSplit = async (orderItemId, splitId) =>
    handlePriceSplitResult(
      await makeDeleteApiCall(
        `shippings/${shippingId}/orderItems/${orderItemId}/priceSplits/${splitId}`,
      ),
      "Shipment price deleted",
    );

  const setProductTemperature = async function (
    productId,
    temperature,
    order_id,
  ) {
    let callResult = await makePostApiCall(`setProductTemperature`, {
      product_id: productId,
      temperature: temperature,
      order_id: order_id,
    });
    if (callResult.success) {
      return callResult;
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
    }
    return callResult;
  };

  const createShippingRecordByID = async function () {
    let callResult = await makePostApiCall(
      `/qaReports/createShippingRecordByID`,
      {
        shippingId: shippingId,
      },
    );
    if (callResult.success) {
      window.open(callResult.data.location, "_blank");
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
      showErrorModal(callResult.data.message);
      return null;
    }
  };

  const printShippingLabelToPrinter = async function (labelPayload) {
    const printResult = await makePostApiCallToPrinter({
      ...labelPayload,
    });
    if (!printResult?.success) {
      const errorMessage =
        printResult?.data?.subtitle ??
        printResult?.data?.message ??
        printResult?.data?.title ??
        "Print failed. Please try again.";
      showErrorModal("Print error", errorMessage);
    }
  };

  const generateAndPrintShippingLabel = async function (orderItem) {
    showProgressModal("Generating and printing shipping label...");
    try {
      const callResult = await makePostApiCall(`generateShippingLabel`, {
        shipping_id: shippingId,
        order_item_id: orderItem.id,
        product_id: orderItem.product_id,
      });

      if (callResult.success) {
        const body = callResult.data;
        const labelPayload =
          body &&
          typeof body === "object" &&
          (body.barcode !== undefined || body.customer_name !== undefined)
            ? body
            : (body?.data ?? body);
        hideProgressModal();
        await printShippingLabelToPrinter(labelPayload);
      } else {
        hideProgressModal();
        if (callResult.errorStatus === 401) {
          logout();
        }
        showErrorModal(callResult.data.message);
        return null;
      }
    } catch (error) {
      hideProgressModal();
      showErrorModal(error?.message ?? "Failed to generate shipping label");
      return null;
    }
  };

  return {
    onFetchOrderItems,
    loading,
    setEmptyModelOpen,
    setSlideOver,
    slideOver,
    emptyModal,
    showErrorModal,
    updateShippingApiCall,
    shipping,
    onFetchPackagedItemByBarcode,
    addPackagedItemToOrderItem,
    getShippingDetails,
    removeShippedCartonApiCall,
    employeeOptions,
    getUsersForDropdown,
    deleteShipping,
    printShipping,
    quickAddPackagedItemToOrderItem,
    fetchPackagedItemsForQuickScan,
    createInvoiceInQb,
    getOrderItemShipments,
    createShippingRecordByID,
    setProductTemperature,
    generateAndPrintShippingLabel,
    onCreatePriceSplit,
    onUpdatePriceSplit,
    onDeletePriceSplit,
  };
}
